import type { IStep, NodeState, SessionVerdict } from 'views/design-system';

/**
 * Fallbacks only. Real labels come from the session's own `design.nodes[]`,
 * which is where "Entity/KYB Document Requirements" and "Individual or
 * Business Questionnaire." live — this map is what a session gets when its
 * pipeline design is missing a node (older sessions, deleted steps).
 */
export const STEP_FALLBACK: Record<string, string> = {
	authentication: 'Liveness & Biometric',
	basicInfo: 'Basic Info',
	form: 'Questionnaire',
	dynamicForm: 'Form',
	kyc: 'KYC (Know your customer)',
	aml: 'AML (Anti-Money Laundering)',
	kyb: 'KYB (Know your business)',
	kybForm: 'Entity/KYB Document Requirements',
	accreditation: 'Accreditation Verification',
	signAgreement: 'Sign Agreement',
	proofReading: 'Document Review',
	successScreen: 'Success Screen',
	payIn: 'Fund (Pay In)',
	payOut: 'Fund (Payout)',
	balanceCheck: 'Balance Check',
	sessionPdf: 'Session PDF',
	event: 'Event',
};

/**
 * The chain reads as a flow diagram, and a diagram wants names not sentences.
 * These are display-only: the label a node carries is the session's OWN design
 * data (`design.nodes[].label`), so renaming it at source would mean rewriting
 * saved flows. Kept in step with OVERVIEW_LABEL in the flow overview
 * (tree-graph-chart/react-flow-graph-chart.tsx), which shortens the same names.
 */
const SHORT_LABEL: Record<string, string> = {
	'Individual or Business Questionnaire.': 'Questionnaire',
	'Individual or Business Questionnaire': 'Questionnaire',
	'Entity/KYB Document Requirements': 'KYB Documents',
	'KYB Verify Business': 'KYB',
	'KYB (Know your business)': 'KYB',
	'KYC (Know your customer)': 'KYC',
	'Accreditation Verification': 'Accreditation',
	'Document Review': 'Doc Review',
	'Liveness & Biometric': 'Liveness',
};

/** exported so the onboarding-flow list names its steps identically */
export const shortStep = (label: string) => SHORT_LABEL[label] ?? label;
const short = shortStep;

const nodeState = (node: any): NodeState => {
	const status = node?.action?.status ?? node?.status;
	/*
	 * THE AUTHENTICATION STEP IS SHOWN BY ITS VERDICT, NOT ITS STATUS.
	 *
	 * `status` on this node means only "the capture finished uploading" —
	 * pipelines writes `completed` the moment a selfie arrives, with no branch
	 * in which it can be anything else. Rendering that as a green pass told a
	 * reviewer that liveness had been established when nothing had adjudicated
	 * anything.
	 *
	 * The verdict lives in `action.data.verification.verdict` and is the only
	 * thing that may turn this chip green. `unverified` — the honest answer
	 * while no adjudicator is wired — renders neutral: the capture happened,
	 * and nothing has judged it.
	 *
	 * Deliberately NOT keyed off the absence of the field: an older session
	 * predating this has no `verification` and must also read neutral rather
	 * than inheriting the old green.
	 */
	const isAuth =
		(node?.stepId ?? node?.action?.stepId) === 'authentication' ||
		node?.key === 'facialRecognition';
	if (isAuth && status === 'completed') {
		const verdict = node?.action?.data?.verification?.verdict;
		if (verdict === 'passed') return 'pass';
		if (verdict === 'failed') return 'fail';
		// 'review' (gestures done, but under the 90% bar, or forced by a device
		// concession) and 'unverified' (no evidence at all) both need a human.
		return 'review';
	}
	if (status === 'completed') return 'pass';
	if (status === 'rejected' || status === 'failed') return 'fail';
	if (status === 'processing') return 'review';
	if (node?.visited) return 'active';
	return 'unreached';
};

/**
 * Nodes arrive flat with parentId links; design nodes carry the labels. Walk
 * the chain so the pills read in flow order, then label each one from the
 * design blob — matched on `key` first (unique per step in a pipeline) and
 * `stepId` second, because a session that predates a step rename keeps the old
 * key while its design carries the new one.
 */
export const toChain = (session: any): IStep[] => {
	const nodes: any[] = session?.nodes ?? [];
	const design: any[] = session?.design?.nodes ?? [];

	const byKey = new Map<string, string>();
	const byStep = new Map<string, string>();
	design.forEach(d => {
		if (d?.key && d?.label) byKey.set(d.key, d.label);
		if (d?.stepId && d?.label && !byStep.has(d.stepId)) byStep.set(d.stepId, d.label);
	});

	const byId = new Map<string, any>(nodes.map(n => [n._id, n]));
	const childrenOf = new Map<string, any[]>();
	const roots: any[] = [];
	nodes.forEach(n => {
		const parents = (n.parentId ?? []).filter((p: string) => byId.has(p));
		if (!parents.length) {
			roots.push(n);
			return;
		}
		const head = parents[0];
		childrenOf.set(head, [...(childrenOf.get(head) ?? []), n]);
	});

	const ordered: any[] = [];
	const seen = new Set<string>();
	const walk = (n: any) => {
		if (seen.has(n._id)) return;
		seen.add(n._id);
		ordered.push(n);
		(childrenOf.get(n._id) ?? []).forEach(walk);
	};
	roots.forEach(walk);
	nodes.forEach(n => !seen.has(n._id) && walk(n));

	return ordered.map(n => ({
		key: n._id,
		label: short(
			byKey.get(n.key) ??
				byStep.get(n.stepId) ??
				STEP_FALLBACK[n.stepId] ??
				n.stepId ??
				'step'
		),
		state: nodeState(n),
	}));
};

export interface IVerdict {
	verdict: SessionVerdict | 'pur';
	label: string;
	edge: 'e-ok' | 'e-rev' | 'e-rej' | 'e-strt' | '';
}

/**
 * The backend's own verdict wins — a session it calls `completed` is approved
 * even while a late step is still settling, and the chain pills carry the
 * per-step truth. Below that, the row status alone is not enough: a session
 * with any node parked in `processing` is under review however it is labelled,
 * and a `pending` session nobody has opened has not started at all.
 */
export const toVerdict = (session: any, chain: IStep[]): IVerdict => {
	const status = session?.status;
	if (status === 'completed' || status === 'approved')
		return { verdict: 'ok', label: 'APPROVED', edge: 'e-ok' };
	if (status === 'rejected' || status === 'failed')
		return { verdict: 'rej', label: 'REJECTED', edge: 'e-rej' };
	if (status === 'processing' || chain.some(c => c.state === 'review'))
		return { verdict: 'rev', label: 'UNDER REVIEW', edge: 'e-rev' };
	if (chain.some(c => c.state !== 'unreached'))
		return { verdict: 'pur', label: 'STARTED', edge: 'e-strt' };
	return { verdict: 'ns', label: 'NOT STARTED', edge: '' };
};

const pad = (n: number) => String(n).padStart(2, '0');

const MONTHS = [
	'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
	'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** "03:12 PM | Aug 20, 2026" — the format the session rows have always used. */
export const stamp = (value?: string | Date | null): string => {
	if (!value) return '--';
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return '--';
	const h = d.getHours();
	const hour = h % 12 === 0 ? 12 : h % 12;
	return `${pad(hour)}:${pad(d.getMinutes())} ${h < 12 ? 'AM' : 'PM'} | ${
		MONTHS[d.getMonth()]
	} ${pad(d.getDate())}, ${d.getFullYear()}`;
};

export const dayTime = (value?: string | Date | null): string => {
	if (!value) return '--';
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return '--';
	const h = d.getHours();
	const hour = h % 12 === 0 ? 12 : h % 12;
	return `${MONTHS[d.getMonth()]} ${pad(d.getDate())}, ${d.getFullYear()} ${pad(
		hour
	)}:${pad(d.getMinutes())} ${h < 12 ? 'AM' : 'PM'}`;
};

/** fundName arrives pipe-joined when a session is assigned to several funds. */
export const primaryFund = (fundName?: string) =>
	(fundName ?? '').split('|')[0] || '';

/**
 * The fund/company name for the Onboarding Details column, WITHOUT the
 * "— step+step+step" suffix the API appends.
 *
 * That suffix is the flow's node list, and the row already prints it in the
 * Status & Onboarding Flow column beside "View complete flow" — and draws it as
 * the step diagram directly above that. Three copies of the same fact in one
 * row, the widest of which pushed the applicant's name and email down.
 *
 * Split on the em dash the API uses, the same way flowName() does, so the two
 * columns agree on where the name ends and the steps begin.
 */
export const fundLabel = (session: any) => {
	const fund = primaryFund(session?.fundName);
	const [name] = fund.split('—');
	return (name ?? fund).trim();
};

export const hasOfacWarning = (session: any) =>
	session?.warning?.aml === true || session?.warning?.am === true;

/** The flow's own name, minus the "— step+step+step" suffix the API appends. */
export const flowName = (session: any) => {
	const raw = session?.onBoardingName ?? '';
	const [name] = raw.split('—');
	return (name ?? raw).trim();
};

export const flowSteps = (session: any) => {
	const raw = session?.onBoardingName ?? '';
	const parts = raw.split('—');
	return parts.length > 1 ? parts.slice(1).join('—').trim() : '';
};
